import React, { useState, useEffect } from 'react';
import { Database, Download, Trash2, HardDrive, Clock, AlertCircle } from 'lucide-react';
import { API_BASE_URL } from '../../config/api';
import { useConfirmModal } from '../common/ConfirmModal';

interface BackupInfo {
  filename: string;
  size: number;
  timestamp: string;
}

export const DatabaseBackup: React.FC = () => {
  const { confirm, ConfirmModal } = useConfirmModal();
  const [backups, setBackups] = useState<BackupInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingBackups, setLoadingBackups] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    fetchBackups();
  }, []);

  const fetchBackups = async () => {
    setLoadingBackups(true);
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('Please log in to access backup features');
        setLoadingBackups(false);
        return;
      }
      
      const response = await fetch(`${API_BASE_URL.replace('/api', '')}/api/backup/list`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.status === 401) {
        setError('Please log in to access backup features');
        return;
      }
      
      if (response.status === 403) {
        setError('You need Admin or Superuser permissions to manage backups');
        return;
      }
      
      const data = await response.json();
      
      if (data.success) {
        setBackups(data.backups);
      } else {
        const errorMessage = data.error?.message || data.error || 'Failed to fetch backups';
        setError(typeof errorMessage === 'string' ? errorMessage : 'Failed to fetch backups');
      }
    } catch (err) {
      setError('Error fetching backups');
      console.error('Error fetching backups:', err);
    } finally {
      setLoadingBackups(false);
    }
  };

  const createBackup = async () => {
    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('Please log in to create backups');
        setLoading(false);
        return;
      }
      
      const response = await fetch(`${API_BASE_URL.replace('/api', '')}/api/backup/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.status === 401) {
        setError('Please log in to create backups');
        setLoading(false);
        return;
      }
      
      if (response.status === 403) {
        setError('You need Admin or Superuser permissions to create backups');
        setLoading(false);
        return;
      }
      
      const data = await response.json();
      
      if (data.success) {
        setSuccessMessage(`Backup created successfully: ${data.filename}`);
        fetchBackups();
      } else {
        const errorMessage = data.error?.message || data.error || 'Failed to create backup';
        setError(typeof errorMessage === 'string' ? errorMessage : 'Failed to create backup');
      }
    } catch (err) {
      setError('Error creating backup');
      console.error('Error creating backup:', err);
    } finally {
      setLoading(false);
    }
  };

  const downloadBackup = (filename: string) => {
    const token = localStorage.getItem('token');
    window.open(`${API_BASE_URL.replace('/api', '')}/api/backup/download/${filename}?token=${token}`, '_blank');
  };

  const deleteBackup = (filename: string) => {
    confirm({
      title: 'Delete Backup',
      message: `Are you sure you want to delete "${filename}"?`,
      confirmText: 'Delete',
      variant: 'danger',
      onConfirm: async () => {
        try {
          const token = localStorage.getItem('token');
          const response = await fetch(`${API_BASE_URL.replace('/api', '')}/api/backup/${filename}`, {
            method: 'DELETE',
            headers: {
              'Authorization': token ? `Bearer ${token}` : '',
            },
          });

          const data = await response.json();

          if (data.success) {
            setSuccessMessage('Backup deleted successfully');
            fetchBackups();
          } else {
            const errorMessage = data.error?.message || data.error || 'Failed to delete backup';
            setError(typeof errorMessage === 'string' ? errorMessage : 'Failed to delete backup');
          }
        } catch (err) {
          setError('Error deleting backup');
          console.error('Error deleting backup:', err);
        }
      }
    });
  };

  const formatFileSize = (bytes: number): string => {
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (timestamp: string): string => {
    return new Date(timestamp).toLocaleString();
  };

  return (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h3 className="mb-0">
          <Database className="me-2" size={24} />
          Database Backup Management
        </h3>
        <button
          className="btn btn-primary"
          onClick={createBackup}
          disabled={loading}
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Creating Backup...
            </>
          ) : (
            <>
              <HardDrive className="me-2" size={18} />
              Create New Backup
            </>
          )}
        </button>
      </div>
      
      <div className="card-body">
        {error && (
          <div className="alert alert-danger d-flex align-items-center" role="alert">
            <AlertCircle className="me-2" size={20} />
            {error}
          </div>
        )}
        
        {successMessage && (
          <div className="alert alert-success d-flex align-items-center" role="alert">
            <Database className="me-2" size={20} />
            {successMessage}
          </div>
        )}

        <div className="mb-3">
          <p className="text-muted">
            Create and manage database backups. Backups are stored as SQL files that can be used to restore the database if needed.
          </p>
        </div>

        <h5 className="mb-3">Existing Backups</h5>
        
        {loadingBackups ? (
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading backups...</span>
            </div>
          </div>
        ) : backups.length === 0 ? (
          <div className="alert alert-info">
            No backups found. Click "Create New Backup" to create your first backup.
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>Filename</th>
                  <th>Size</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {backups.map((backup) => (
                  <tr key={backup.filename}>
                    <td>
                      <Database className="me-2" size={16} />
                      {backup.filename}
                    </td>
                    <td>{formatFileSize(backup.size)}</td>
                    <td>
                      <Clock className="me-2" size={16} />
                      {formatDate(backup.timestamp)}
                    </td>
                    <td>
                      <button
                        className="btn btn-sm btn-outline-primary me-2"
                        onClick={() => downloadBackup(backup.filename)}
                        title="Download backup"
                      >
                        <Download size={16} />
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => deleteBackup(backup.filename)}
                        title="Delete backup"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-4">
          <div className="alert alert-warning">
            <strong>Important:</strong> 
            <ul className="mb-0 mt-2">
              <li>Backups contain all database data including user information</li>
              <li>Store backup files securely and do not share them publicly</li>
              <li>Regular backups are recommended before major changes</li>
              <li>To restore a backup, you'll need database access and the psql command</li>
            </ul>
          </div>
        </div>
      </div>
      <ConfirmModal />
    </div>
  );
};